/*
 * The step 1 form on a customer's website (resources/js/library): plain CSS, scoped to .scrap-local-form so it never touches the page.
 * It is drawn in a shadow root, so these styles are the only ones it has and the page's cannot reach it. It wears the form's own design tokens: the library sets the same CSS variables the renderer's pages use (--color-brand, ...) on the
 * form, and each has the library's own value as its fallback, so a form that sets none looks as it always has. The plate yellow and the
 * blue and red badges are fixed on purpose, as the UK plate colours are in the renderer.
 */
:host {
    display: block;
}

/* Held back, keeping its place, until the form's colours have arrived. */
.scrap-local-form.is-loading {
    visibility: hidden;
}

.scrap-local-form {
    width: 100%;
    margin: 0 auto;
    /* Without a font of its own the form takes the page's. */
    font-family: var(--font-sans, inherit);
    box-sizing: border-box;
}

.scrap-local-form *,
.scrap-local-form *::before,
.scrap-local-form *::after {
    box-sizing: border-box;
}

.scrap-local-form .form-fields {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
}

.scrap-local-form .form-fields.vertical-layout {
    max-width: 440px;
    flex-direction: column;
}

.scrap-local-form .form-fields.horizontal-layout {
    max-width: 1280px;
    flex-direction: row;
}

.scrap-local-form .form-controls {
    display: flex;
    justify-content: center;
    align-items: center;
}

.scrap-local-form .form-controls button {
    width: 100%;
}

/* A vertical form's button is no wider than its fields, and sits under them. */
.scrap-local-form .form-controls.vertical-layout {
    justify-content: flex-start;
}

.scrap-local-form .form-controls.vertical-layout button {
    max-width: 440px;
}

.scrap-local-form .form-controls.horizontal-layout button {
    max-width: 320px;
}

@media screen and (max-width: 960px) {
    .scrap-local-form .form-fields.horizontal-layout {
        max-width: 440px;
        margin: 0 auto 10px auto;
        flex-direction: column;
    }

    .scrap-local-form .form-controls.horizontal-layout button {
        max-width: 440px;
    }
}

.scrap-local-form .form-field-wrapper {
    width: 100%;
}

.scrap-local-form .field-error,
.scrap-local-form .form-error {
    display: none;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-danger, rgb(204, 106, 76));
    background-color: color-mix(in srgb, var(--color-danger, rgb(204, 106, 76)) 10%, #fff);
    border-radius: 0.375rem;
    text-align: left;
    padding: 8px;
    margin-top: 5px;
}

.scrap-local-form .form-error {
    max-width: 440px;
    margin: 0 auto 10px auto;
}

.scrap-local-form .form-field-wrapper input,
.scrap-local-form .form-field-wrapper select {
    text-transform: uppercase;
    font-weight: 700;
    width: 100%;
    height: 70px;
    padding: 0 20px;
    font-family: inherit;
    font-size: 1.25rem;
    line-height: 1.75rem;
    letter-spacing: 0.1em;
    border: 1px solid rgb(229, 231, 235);
    border-radius: 0 var(--radius-control, 8px) var(--radius-control, 8px) 0;
    color: var(--color-ink, black);
    background-color: var(--color-surface, #fff);
    outline: none;
}

.scrap-local-form .form-field-wrapper input:focus-visible {
    border-color: var(--color-brand, rgb(17, 168, 135));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand, rgb(17, 168, 135)) 25%, transparent);
}

.scrap-local-form .form-field-wrapper input[aria-invalid='true'] {
    border-color: var(--color-danger, rgb(204, 106, 76));
}

.scrap-local-form .form-field-wrapper input::placeholder {
    text-transform: uppercase;
    font-weight: 700;
    color: var(--color-muted, rgb(145, 145, 145));
}

.scrap-local-form .form-field-wrapper input.email-field-input {
    text-transform: lowercase;
}

.scrap-local-form .field-with-icon {
    display: flex;
    width: 100%;
    flex-direction: row;
}

.scrap-local-form .field-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 70px;
    height: 70px;
    border-radius: var(--radius-control, 8px) 0 0 var(--radius-control, 8px);
    background-color: var(--color-muted, rgb(145, 145, 145));
}

.scrap-local-form .field-icon-wrapper.vrm-field-icon-wrapper {
    background-color: rgb(37, 99, 235);
}

.scrap-local-form .field-icon-wrapper.postcode-field-icon-wrapper {
    background-color: rgb(220, 38, 38);
}

.scrap-local-form .field-input-wrapper {
    flex-grow: 1;
}

.scrap-local-form .vrm-field-wrapper input {
    background-color: rgb(254, 213, 54);
}

.scrap-local-form .vrm-field-wrapper input::placeholder {
    color: #000;
}

.scrap-local-form button[type='submit'] {
    color: var(--color-brand-contrast, white);
    font-family: inherit;
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1rem;
    letter-spacing: 0.05rem;
    cursor: pointer;
    padding: 1.25rem 0;
    border-radius: var(--radius-button, 9999px);
    background-color: var(--color-brand, rgb(17, 168, 135));
    border: none;
    outline: none;
}

.scrap-local-form button[type='submit']:focus-visible {
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand, rgb(17, 168, 135)) 35%, transparent);
}

.scrap-local-form button[type='submit']:disabled {
    opacity: 0.7;
    cursor: progress;
}
